/**
 * useWorkView Hook
 *
 * Provides work view mode state and filtering logic for seamlessly
 * switching between "My Work" and "Team" views.
 *
 * Uses a shared Context so all consumers (toggle, pages) share the same viewMode.
 */

import { createElement, createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSession } from './useSession'
import { getMyWorkFilter } from '../api/agencyTeam'
import type { WorkViewMode } from '../types/agencyTeam'
import type { AppUser } from '../types/user'
import { escapeFilterValue } from '../lib/pocketbase'
import { setPref, type UserPrefs } from '../utils/userPreferences'
import { viewModesForRole } from '../utils/athleteScope'

const WORK_VIEW_STORAGE_KEY = 'dealpoint_work_view_mode'

/**
 * A saved mode the role may not use falls back to the role's first mode: an
 * agent whose browser remembers 'team' from before the role rule must not
 * see the agency through it.
 */
function allowedMode(mode: WorkViewMode, role: string | null | undefined): WorkViewMode {
  const modes = viewModesForRole(role)
  return modes.includes(mode) ? mode : modes[0]
}

// --- Context for shared viewMode state ---

type WorkViewContextValue = {
  viewMode: WorkViewMode
  setViewMode: (mode: WorkViewMode) => void
}

const WorkViewContext = createContext<WorkViewContextValue | null>(null)

export function WorkViewProvider({ children }: { children: ReactNode }) {
  const { user } = useSession()
  const userRole = (user as AppUser | null)?.role

  const [viewMode, setViewModeInternal] = useState<WorkViewMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(WORK_VIEW_STORAGE_KEY)
      if (saved === 'my-work' || saved === 'team' || saved === 'all') {
        return saved
      }
    }
    return 'my-work'
  })

  // Once the role is known: admins with no saved choice start on the agency
  // view; anyone holding a mode their role may not use is moved off it.
  useEffect(() => {
    if (!userRole) return
    setViewModeInternal(current => {
      const saved = localStorage.getItem(WORK_VIEW_STORAGE_KEY)
      if (!saved && (userRole === 'superadmin' || userRole === 'admin')) return 'team'
      return allowedMode(current, userRole)
    })
  }, [userRole])

  // Pick up work_view_mode when server preferences finish loading (cross-device sync)
  useEffect(() => {
    const handler = (e: Event) => {
      const prefs = (e as CustomEvent<UserPrefs>).detail
      if (prefs.work_view_mode) {
        setViewModeInternal(allowedMode(prefs.work_view_mode, userRole))
      }
    }
    window.addEventListener('dealpoint:preferences-loaded', handler)
    return () => window.removeEventListener('dealpoint:preferences-loaded', handler)
  }, [userRole])

  const setViewMode = useCallback((requested: WorkViewMode) => {
    const mode = allowedMode(requested, userRole)
    setViewModeInternal(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem(WORK_VIEW_STORAGE_KEY, mode)
      setPref('work_view_mode', mode)
    }
  }, [userRole])

  const value = useMemo(() => ({ viewMode, setViewMode }), [viewMode, setViewMode])

  return createElement(WorkViewContext.Provider, { value }, children)
}

// --- Hook that consumes shared viewMode + derives filters ---

export function useWorkView() {
  const ctx = useContext(WorkViewContext)
  if (!ctx) {
    throw new Error('useWorkView must be used within a WorkViewProvider')
  }

  const { viewMode, setViewMode } = ctx
  const { user } = useSession()

  // Get user info
  const appUser = user as AppUser | null
  const userId = appUser?.id
  const agencyId = appUser?.agency_id
  const userRole = appUser?.role

  // Admins and superadmins can see all
  const isAdmin = userRole === 'admin' || userRole === 'superadmin'

  /**
   * Get PocketBase filter string for the current view mode
   */
  const getFilter = useCallback((collection: 'clients' | 'deals' | 'prospects'): string => {
    if (!userId) {
      return 'id = ""'
    }

    const isSuperAdmin = userRole === 'superadmin'

    // Superadmins have cross-agency access
    if (isSuperAdmin) {
      switch (viewMode) {
        case 'my-work':
          return getMyWorkFilter(userId, collection)
        case 'team':
          // Team = current agency only
          return agencyId ? `agency_id = "${escapeFilterValue(agencyId)}"` : ''
        case 'all':
        default:
          // All = everything across all agencies
          return ''
      }
    }

    if (!agencyId) {
      return 'id = ""'
    }

    const safeAgencyId = escapeFilterValue(agencyId)

    switch (viewMode) {
      case 'my-work':
        return `(${getMyWorkFilter(userId, collection)}) && agency_id = "${safeAgencyId}"`

      case 'team':
        return `agency_id = "${safeAgencyId}"`

      case 'all':
        return `agency_id = "${safeAgencyId}"`

      default:
        return `agency_id = "${safeAgencyId}"`
    }
  }, [userId, agencyId, viewMode, userRole])

  const getTeamFilterAsync = useCallback((collection: 'clients' | 'deals' | 'prospects'): string => {
    if (!userId) {
      return 'id = ""'
    }

    const isSuperAdmin = userRole === 'superadmin'
    if (isSuperAdmin) {
      return agencyId && viewMode === 'team' ? `agency_id = "${escapeFilterValue(agencyId)}"` : ''
    }

    if (!agencyId) return 'id = ""'

    return getFilter(collection)
  }, [userId, agencyId, viewMode, getFilter, userRole])

  const getTaskFilter = useCallback((): string => {
    if (!userId) {
      return 'id = ""'
    }

    const isSuperAdmin = userRole === 'superadmin'
    const safeUserId = escapeFilterValue(userId)

    if (isSuperAdmin) {
      if (viewMode === 'my-work') {
        return `(assigned_to = "${safeUserId}" || created_by = "${safeUserId}")`
      }
      if (viewMode === 'team' && agencyId) {
        return `agency_id = "${escapeFilterValue(agencyId)}"`
      }
      return ''
    }

    if (!agencyId) return 'id = ""'

    const safeAgencyId = escapeFilterValue(agencyId)

    switch (viewMode) {
      case 'my-work':
        return `(assigned_to = "${safeUserId}" || created_by = "${safeUserId}") && agency_id = "${safeAgencyId}"`

      case 'team':
        return `agency_id = "${safeAgencyId}"`

      case 'all':
        return `agency_id = "${safeAgencyId}"`

      default:
        return `agency_id = "${safeAgencyId}"`
    }
  }, [userId, agencyId, viewMode, userRole])

  // Who may see the agency: admins and superadmins by choice; assistants and
  // read-only users always (they own nothing); agents never.
  const availableViewModes = useMemo((): WorkViewMode[] => viewModesForRole(userRole), [userRole])

  // The same words the Book, Prospects and Pipeline use for the same choice.
  const viewModeLabels: Record<WorkViewMode, string> = {
    'my-work': 'Mine',
    'team': 'Everyone',
    'all': 'All agencies',
  }

  return {
    viewMode,
    setViewMode,
    getFilter,
    getTeamFilterAsync,
    getTaskFilter,
    availableViewModes,
    viewModeLabels,
    isAdmin,
    userId,
    agencyId,
  }
}
